import * as React from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { SliderHandle } from './components/slider-handle/slider-handle'
import { ResizeContext } from './components/multi-slider/multi-slider'
import { Pane } from './components/pane/pane'
import { clamp, throttle } from 'lodash'

interface SliderState {
  resizeSecondaryPane: boolean
  secondaryPaneSize: number
}

export interface SliderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onResize'> {
  initialSize?: number
  isVertical?: boolean
  allowResize?: boolean
  secondaryPaneMinSize?: number
  secondaryPaneMaxSize?: number
  firstPaneClassName?: string
  secondPaneClassName?: string
  sidebarClassName?: string
  onResize?: (resize: number, fullSize: number) => void
}

const SliderContainer = styled.div<{ isVertical: boolean }>`
  display: grid;
  height: 100%;

  ${({ isVertical }) =>
    isVertical
      ? css`
          grid-template-rows: 1fr min-content auto;
        `
      : css`
          grid-template-columns: 1fr min-content auto;
        `}
`

const Sidebar = styled.div<Pick<SliderProps, 'allowResize' | 'isVertical'>>`
  align-self: center;
  justify-self: center;
  display: block;
  position: relative;
  z-index: 2;

  display: ${({ allowResize }) => !allowResize && 'none'};
  transform: ${({ isVertical }) => isVertical && 'rotate(-90deg)'};
`

const Move = styled.div`
  position: fixed;
  z-index: 10;
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;
`

export function getPanesSize(
  containerRef: React.RefObject<HTMLDivElement>,
  resizerRef: React.RefObject<HTMLDivElement>,
  isVertical = false
) {
  if (containerRef.current && resizerRef.current) {
    const contentSize = containerRef.current.getBoundingClientRect()
    const resizerSize = resizerRef.current.getBoundingClientRect()

    if (isVertical) {
      return contentSize.height - resizerSize.height
    } else {
      return contentSize.width - resizerSize.width
    }
  }
  return 0
}

interface ResizeContextType {
  setResize?: (resize: boolean) => void
  resize?: boolean
}

export class Slider extends React.Component<SliderProps, SliderState> {
  context: ResizeContextType
  containerRef: React.RefObject<HTMLDivElement>
  resizerRef: React.RefObject<HTMLDivElement>

  constructor(props: SliderProps) {
    super(props)
    this.containerRef = React.createRef<HTMLDivElement>()
    this.resizerRef = React.createRef<HTMLDivElement>()
  }

  state = {
    resizeSecondaryPane: false,
    secondaryPaneSize: 0,
  }

  private resizeCallback(resize: number, fullSize: number) {
    this.props.onResize?.(resize, fullSize)
  }

  private onResizeWindow = throttle(() => {
    const panesSize = getPanesSize(
      this.containerRef,
      this.resizerRef,
      this.props.isVertical
    )

    const { secondaryPaneMaxSize = panesSize, secondaryPaneMinSize = 0 } =
      this.props

    const secondaryPaneSize = clamp(
      this.state.secondaryPaneSize,
      secondaryPaneMinSize,
      secondaryPaneMaxSize
    )

    this.setState({ secondaryPaneSize })

    this.resizeCallback(secondaryPaneSize, panesSize)
  }, 200)

  private getChildToArray(children: React.ReactNode) {
    return React.Children.toArray(children)
      .filter((c) => c)
      .slice(0, 2)
  }

  componentDidUpdate(prevProps: Readonly<SliderProps>) {
    if (this.context.resize) {
      this.onResizeWindow()
    }

    if (!this.containerRef && !this.resizerRef) {
      return
    }

    if (
      this.getChildToArray(prevProps.children).length !==
        this.getChildToArray(this.props.children).length ||
      this.props.initialSize !== prevProps.initialSize
    ) {
      const {
        secondaryPaneMaxSize,
        secondaryPaneMinSize = 0,
        initialSize,
        isVertical,
      } = this.props

      const panesSize = getPanesSize(
        this.containerRef,
        this.resizerRef,
        isVertical
      )

      const size = initialSize || panesSize / 2
      const maxPaneSize = secondaryPaneMaxSize || panesSize
      const secondaryPaneSize = clamp(size, secondaryPaneMinSize, maxPaneSize)

      this.setState({ secondaryPaneSize })
    }
  }

  componentDidMount() {
    window.addEventListener('resize', this.onResizeWindow)
    const { initialSize, isVertical } = this.props
    const panesSize = getPanesSize(
      this.containerRef,
      this.resizerRef,
      isVertical
    )
    const secondaryPaneSize = initialSize || panesSize / 2

    this.setState({
      secondaryPaneSize,
    })

    this.resizeCallback(secondaryPaneSize, panesSize)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResizeWindow)
  }

  render() {
    const {
      isVertical = false,
      allowResize = true,
      firstPaneClassName,
      secondPaneClassName,
      sidebarClassName,
    } = this.props

    const childrenArray = this.getChildToArray(this.props.children)

    if (childrenArray.length === 0) {
      childrenArray.push(<div />)
    }

    if (childrenArray.length > 2) {
      console.warn('Slider component supports maximum 2 children')
      return null
    }

    return (
      <SliderContainer ref={this.containerRef} isVertical={isVertical}>
        <Pane primary className={firstPaneClassName} isVertical={isVertical}>
          {childrenArray[0]}
        </Pane>
        {childrenArray.length > 1 && (
          <>
            <Sidebar
              allowResize={allowResize}
              isVertical={isVertical}
              data-e2e={`slider__handler`}
              ref={this.resizerRef}
              onMouseDown={this.onMouseDown}
              onTouchStart={this.onMouseDown}
              className={sidebarClassName}
            >
              <SliderHandle isVertical={isVertical} />
            </Sidebar>
            {this.state.resizeSecondaryPane && (
              <Move
                onMouseMove={this.onMouseMove}
                onTouchMove={this.onTouchMove}
                onMouseUp={this.onMouseUp}
                onTouchEnd={this.onMouseUp}
              />
            )}
            <Pane
              isVertical={isVertical}
              allowResize={allowResize}
              className={secondPaneClassName}
              size={this.state.secondaryPaneSize}
            >
              {childrenArray[1]}
            </Pane>
          </>
        )}
      </SliderContainer>
    )
  }

  private onMouseDown = () => {
    if (this.context.setResize) {
      this.context.setResize(true)
    }

    this.setState({ resizeSecondaryPane: true })
  }

  private onMouseUp = () => {
    if (this.context.setResize) {
      this.context.setResize(false)
    }

    this.setState({ resizeSecondaryPane: false })
  }

  private onMouseMove = (e: React.MouseEvent | React.Touch) => {
    const { resizeSecondaryPane } = this.state

    if (resizeSecondaryPane && this.containerRef && this.resizerRef) {
      const {
        secondaryPaneMaxSize,
        secondaryPaneMinSize = 0,
        isVertical,
      } = this.props

      const resizerRect = this.resizerRef.current.getBoundingClientRect()

      const delta = isVertical
        ? resizerRect.bottom - resizerRect.height / 2 - e.clientY
        : resizerRect.right - resizerRect.width / 2 - e.clientX

      const size = this.state.secondaryPaneSize + delta
      const maxPaneSize =
        secondaryPaneMaxSize ||
        getPanesSize(this.containerRef, this.resizerRef, isVertical)
      const secondaryPaneSize = clamp(size, secondaryPaneMinSize, maxPaneSize)

      this.setState({ secondaryPaneSize })

      this.resizeCallback(secondaryPaneSize, maxPaneSize)
    }
  }

  private onTouchMove = (e: React.TouchEvent) => {
    this.onMouseMove(e.touches[0])
  }
}

Slider.contextType = ResizeContext
